import React, { Component } from "react";
import PropTypes from "prop-types";
import { DAYS_OF_WEEK, RECURRENCE_FREQUENCIES } from "@constants";
import utils from "@utils";
import Loader from "../../Loader/Loader";
import Modal from "../../Modal/Modal";

class AssignmentConfirmationModal extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			recurrenceType: "one",
			selectedDays: [],
		};
	}

	onExited = () => this.setState({ loading: false, recurrenceType: "one", selectedDays: [] });

	/** Confirms officer assignment and passes the data required to update as a parameter to the prop `onConfirm` */
	confirmAssignment = () => {
		const { recurrenceType, selectedDays } = this.state;
		const {
			assignmentProps: {
				officer: { Id: userId },
			},
			onConfirm,
		} = this.props;
		if (recurrenceType === "days" && selectedDays.length === 0) {
			utils.notify({
				type: "error",
				message: "At least one day must be selected",
				timeOut: 2000,
			});
		} else {
			this.setState({ loading: true });
			try {
				const params = {
					userId,
					updateAllUpcoming: recurrenceType !== "one",
					AssignmentRepeatsOn: recurrenceType === "all" ? "All" : selectedDays.join(),
				};
				onConfirm(params);
			} catch (err) {
				this.setState({ loading: false });
			}
		}
	};

	/**
	 * Handles changes to which days an officer should be assigned to a recurring job. If the day is already included, it is removed and vice-versa.
	 * @param {String} day The short name of the day to update ('mon', 'wed', 'fri', etc.)
	 */
	handleDayChange = day => {
		let { selectedDays } = this.state;
		if (selectedDays.includes(day)) selectedDays = selectedDays.filter(item => item !== day);
		else selectedDays.push(day);
		this.setState({ selectedDays });
	};

	/** Renders which days of the week the officer can be assigned to a job as per the current recurrence rule */
	renderAvailableDays = () => {
		const { selectedDays } = this.state;
		const {
			assignmentProps: { recurrenceRule },
		} = this.props;

		let availableDays = DAYS_OF_WEEK;

		if (recurrenceRule) {
			if (recurrenceRule.RepeatType === RECURRENCE_FREQUENCIES.WEEKDAYS.value) {
				availableDays = DAYS_OF_WEEK.filter(item => item.frequencyType === "weekday");
			} else if (recurrenceRule.RepeatType === RECURRENCE_FREQUENCIES.WEEKENDS.value) {
				availableDays = DAYS_OF_WEEK.filter(item => item.frequencyType === "weekend");
			} else if (
				recurrenceRule.RepeatType === RECURRENCE_FREQUENCIES.WEEKLY.value &&
				recurrenceRule.RepeatsOn
			) {
				availableDays = recurrenceRule.RepeatsOn.split(",").map(item =>
					DAYS_OF_WEEK.find(day => item.toLowerCase() === day.shortName),
				);
			}
		}

		return (
			<div className="row form-group">
				<div className="col-xs-12">
					<For
						each="day"
						of={DAYS_OF_WEEK}
					>
						<div
							key={`unassign-day-${day.shortName}`}
							className="DOW-container"
						>
							<input
								type="checkbox"
								name={`DOW_${day.shortName}`}
								id={`DOW_${day.shortName}`}
								disabled={!availableDays.includes(day)}
								checked={selectedDays.indexOf(day.shortName) !== -1}
								onChange={() => this.handleDayChange(day.shortName)}
							/>
							<label
								htmlFor={`DOW_${day.shortName}`}
								title={day.label}
							>
								{day.label.charAt(0)}
							</label>
						</div>
					</For>
				</div>
			</div>
		);
	};

	render() {
		const { lang } = global;
		const { recurrenceType, loading } = this.state;
		const {
			assignmentProps: {
				show,
				recurrenceRule,
				officer: { FirstName, LastName },
			},
			onClose,
		} = this.props;
		return (
			<Modal
				show={show}
				onExited={this.onExited}
				backdrop="static"
			>
				<Modal.Header>
					<Modal.Title>{lang.t("Recursive Job Confirmation")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="row">
						<p className="col-sm-12 text-danger">
							{lang.t("dynamic.Assigning officer to a recurring job", {
								FirstName,
								LastName,
							})}
						</p>
						<div className="col-sm-12">
							{lang.t("How do you want to assign this officer_")}
						</div>
					</div>
					<div className="row form-group">
						<div className="col-sm-12">
							<div className="radio">
								<label htmlFor="recursive_one">
									<input
										type="radio"
										name="recursiveType"
										id="recursive_one"
										onChange={() => this.setState({ recurrenceType: "one" })}
										checked={recurrenceType === "one"}
									/>
									{lang.t("Only this job")}
								</label>
							</div>
							<div className="radio">
								<label htmlFor="recursive_all">
									<input
										type="radio"
										name="recursiveType"
										id="recursive_all"
										onChange={() => this.setState({ recurrenceType: "all" })}
										checked={recurrenceType === "all"}
									/>
									{lang.t("All upcoming jobs in this series")}
								</label>
							</div>
							<If
								condition={
									recurrenceRule.RepeatType !==
									RECURRENCE_FREQUENCIES.CUSTOM.value
								}
							>
								<div className="radio">
									<label htmlFor="recursive_days">
										<input
											type="radio"
											name="recursiveType"
											id="recursive_days"
											onChange={() =>
												this.setState({ recurrenceType: "days" })
											}
											checked={recurrenceType === "days"}
										/>
										{lang.t(
											"Assign this officer to the following days of the week in this series",
										)}
										:
									</label>
								</div>
							</If>
						</div>
					</div>
					<If condition={recurrenceType === "days"}>{this.renderAvailableDays()}</If>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						className="btn btn-link"
						onClick={onClose}
					>
						{lang.t("Cancel")}
					</button>
					<button
						type="button"
						className="btn btn-primary"
						onClick={this.confirmAssignment}
					>
						{lang.t("Continue")}
					</button>
				</Modal.Footer>
				<Loader active={loading} />
			</Modal>
		);
	}
}

AssignmentConfirmationModal.propTypes = {
	assignmentProps: PropTypes.shape({
		show: PropTypes.bool,
		recurrenceRule: PropTypes.object,
		officer: PropTypes.object,
	}),
	onConfirm: PropTypes.func,
	onClose: PropTypes.func,
};

AssignmentConfirmationModal.defaultProps = {
	assignmentProps: {
		show: false,
		recurrenceRule: {},
		officer: {},
	},
	onConfirm: () => {},
	onClose: () => {},
};

export default AssignmentConfirmationModal;
